import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Clock, Star, Users } from "lucide-react";
import BookingWidget from "./BookingWidget";
import Navbar from "./Navbar";
import { supabase } from "@/integrations/supabase/client";

const HeroSection = () => {
  const [slides, setSlides] = useState<string[]>([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    supabase
      .from("hero_slides")
      .select("image_url")
      .eq("is_active", true)
      .order("sort_order")
      .then(({ data }) => {
        if (data && data.length > 0) setSlides(data.map((d) => d.image_url));
      });
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setCurrent((c) => (c + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const badges = [
    { icon: Shield, text: "Licensed & Insured" },
    { icon: Clock, text: "Always On Time" },
    { icon: Star, text: "5-Star Rated" },
    { icon: Users, text: "10K+ Travelers" },
  ];

  return (
    <section className="relative min-h-screen pt-20 overflow-hidden">
      <Navbar />

      {/* Full hero background slider */}
      {slides.length > 0 && (
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0 z-0"
          >
            <img
              src={slides[current]}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-white/75" />
          </motion.div>
        </AnimatePresence>
      )}

      {/* Fallback bg if no slides */}
      {slides.length === 0 && (
        <div className="absolute inset-0 z-0 bg-muted/30" />
      )}

      <div className="relative z-10 container mx-auto px-4 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left: Text content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-primary/10 backdrop-blur-sm rounded-md px-4 py-2 mb-6"
            >
              <Star className="w-4 h-4 text-primary fill-primary" />
              <span className="text-primary font-medium text-sm">
                #1 Tour & Transfer Service in DR
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
            >
              Your Caribbean
              <br />
              <span className="text-primary">Adventure Awaits</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-muted-foreground text-lg max-w-lg mb-8 leading-relaxed"
            >
              Explore Dominican Republic with premium tours and reliable airport
              transfers. Always on time — your adventure starts here.
            </motion.p>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="grid grid-cols-2 gap-3"
            >
              {badges.map((badge, i) => (
                <motion.div
                  key={badge.text}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-border/50"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <badge.icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-foreground text-sm font-medium">{badge.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right: Booking Widget */}
          <div id="booking">
            <BookingWidget />
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      {slides.length > 1 && (
        <div className="relative z-10 flex justify-center gap-2 pb-6">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === current ? "bg-primary w-8" : "bg-foreground/20 w-3"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroSection;
